'use client';

import React from 'react';

import { cn } from '~/lib/utils';

import { Mood, moods } from '../_data/data';
import { GameIntro } from '../../_components/game-intro';
import { BreathingGameMoodCard } from './breathing-game-mood-card';

interface BreathingGameBoardProps {
  finishGame: () => void;
}

export function BreathingGameBoard(props: BreathingGameBoardProps) {
  const [selectedMoods, setSelectedMoods] = React.useState<Mood[]>([]);
  const [finishedVideo, setFinishedVideo] = React.useState<boolean>(false);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const checkCompletion = React.useCallback(() => {
    if (selectedMoods.length === 2) {
      props.finishGame();
    }
  }, [props, selectedMoods]);

  const handleCardClick = React.useCallback(
    (mood: Mood) => {
      if (selectedMoods.length === 1) {
        setSelectedMoods((prev) => [...prev, mood]);
      } else {
        setTimeout(() => videoRef.current?.play().catch(() => null), 1000);
        setSelectedMoods([mood]);
      }
    },
    [selectedMoods.length],
  );

  const handleVideoEnd = React.useCallback(() => {
    if (videoRef.current) videoRef.current.style.opacity = '0';
    setTimeout(() => setFinishedVideo(true), 1000);
  }, []);

  React.useEffect(() => {
    checkCompletion();
  }, [checkCompletion, selectedMoods]);

  return (
    <>
      {finishedVideo && (
        <GameIntro
          title='Feel Different?'
          description='Sometimes taking a moment to focus on your breath can change how you feel.'
        />
      )}
      {selectedMoods.length === 1 && !finishedVideo && (
        <GameIntro
          title="Let's Try a Breathing Exercise"
          description='Breathe in as the shape gets bigger. Breathe out as the shape gets smaller.'
        />
      )}
      {selectedMoods.length === 0 && (
        <GameIntro
          title='Breathing Exercises'
          description='Take a moment to check in with yourself and relax through these breathing exercises.'
        />
      )}
      <div className='grid place-items-center'>
        <video
          aria-hidden='true'
          className={cn(
            'col-start-1 row-start-1 h-80 w-80 opacity-100 transition-opacity duration-1000',
            selectedMoods.length === 1 && !finishedVideo ? 'opacity-100' : 'opacity-0',
          )}
          muted
          onEnded={handleVideoEnd}
          playsInline
          preload='auto'
          ref={videoRef}
        >
          <source src='/video/game-breathe.mp4' type='video/mp4' />
        </video>
        {(selectedMoods.length === 0 || finishedVideo) && (
          <figure className='relative col-start-1 row-start-1 grid grid-cols-3 items-center gap-x-6 gap-y-4'>
            {moods.map((mood) => (
              <BreathingGameMoodCard key={mood.id} mood={mood} onClick={handleCardClick} />
            ))}
          </figure>
        )}
      </div>
    </>
  );
}
